import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Brain, ChevronLeft, LogIn, LogOut, AlertTriangle, RefreshCw } from "lucide-react";
import { api, devRole, assumeRole, isEmbedded } from "./api";
import { C, ROLE_META, Badge } from "./theme";
import { T, t, LanguagePicker } from "./i18n.jsx";
import Landing, { HeroStage } from "./Landing.jsx";
import AuthPanel from "./AuthPanel.jsx";

// Code-split the heavy, on-demand surfaces so the initial bundle stays small. The Studio
// (App.jsx) carries recharts and is only opened by researcher/admin; each dashboard is
// only needed for its own role. Landing stays eager — it is the first paint.
const Studio = lazy(() => import("./App.jsx"));
const JoinPage = lazy(() => import("./JoinPage.jsx"));
const AdminDashboard = lazy(() => import("./dashboards/AdminDashboard.jsx"));
const ResearcherDashboard = lazy(() => import("./dashboards/ResearcherDashboard.jsx"));
const ExperimenterDashboard = lazy(() => import("./dashboards/ExperimenterDashboard.jsx"));
const ParticipantDashboard = lazy(() => import("./dashboards/ParticipantDashboard.jsx"));

/* ------------------------------------------------------------------ *
 * Shell — the role-aware entry point. Loads /api/me, then renders the
 * dashboard for the caller's appRole. A dev role switcher (honoured by
 * the server only when there is no real OAuth session) lets all four
 * dashboards be previewed from one browser. Researcher/Admin can open
 * the full Experiment Studio (App.jsx) to design a spec.
 * ------------------------------------------------------------------ */

const DASHBOARDS = {
  "system-console": AdminDashboard,
  "research": ResearcherDashboard,
  "sessions": ExperimenterDashboard,
  "my-studies": ParticipantDashboard,
};

const ROLE_ORDER = ["admin", "researcher", "experimenter", "participant"];

// QR self-enrolment deep link: /join/<slug> renders the standalone JoinPage.
const joinSlug = (() => {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/join\/([a-z0-9_-]+)/i);
  return m ? m[1] : null;
})();

// Login popup: when the app is embedded, OAuth login runs in a top-level popup (see
// api.login). After the callback lands the popup back on our origin, this window's name is
// still "onset-login" — notify the embedding opener that the session is live and close.
const isLoginPopup = (() => {
  if (typeof window === "undefined") return false;
  try { return window.name === "onset-login" && !!window.opener && !window.opener.closed; }
  catch { return false; }
})();

// Studio deep link: /studio/<slug> opens the Experiment Studio on that experiment
// (the editing surface — distinct from /join, which is the participant enrolment page).
const studioSlug = (() => {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/studio\/([a-z0-9_-]+)/i);
  return m ? m[1] : null;
})();

export default function Shell() {
  const [me, setMe] = useState(null);
  const [session, setSession] = useState(null);
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState(null);
  const [studio, setStudio] = useState(studioSlug ? { slug: studioSlug } : null); // { slug } when the Studio is open
  const [entered, setEntered] = useState(false); // dev: passed the landing page
  const [showAuth, setShowAuth] = useState(false); // register/sign-in modal

  const load = useCallback(() => {
    setErr(null);
    api.me().then(setMe).catch((e) => setErr(e.message));
    api.session().then(setSession).catch(() => setSession({ authenticated: false }));   // resolve (never stay "loading") on error
    api.health().then(setHealth).catch(() => setHealth(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Login-popup side: session is now established on our origin — tell the embedding opener
  // to refresh, then close this window.
  useEffect(() => {
    if (!isLoginPopup) return;
    try { window.opener.postMessage({ type: "onset:authed" }, window.location.origin); } catch { /* opener gone */ }
    window.close();
  }, []);

  // Embedded (opener) side: when the login popup reports success, refresh identity in place.
  useEffect(() => {
    if (!isEmbedded) return;
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== "onset:authed") return;
      setShowAuth(false); setEntered(true); load();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [load]);

  // Authenticated users "act as" a lower role (x-assume-role); dev sessions pick any role (x-dev-role).
  const authedNow = () => !!(session && session.authenticated);
  const switchRole = (r) => { (authedNow() ? assumeRole : devRole).set(r); setMe(null); setStudio(null); load(); };
  const signOut = () => { assumeRole.set(""); api.logout().then(() => { setEntered(false); load(); }).catch(() => { setEntered(false); load(); }); };
  const onAuthed = () => { setShowAuth(false); setEntered(true); load(); };

  // Login-popup window: the effect above already messaged the opener and called close(); show
  // a graceful fallback in case the browser blocked the programmatic close.
  if (isLoginPopup) {
    return (
      <Center>
        <Brain size={26} color={C.indigo} />
        <T comp="div" style={{ color: C.hi }}>You're signed in.</T>
        <T comp="div" style={{ color: C.dim, fontSize: 13 }}>You can close this window and return to the app.</T>
      </Center>
    );
  }

  // Wait only for the FAST session check (/auth/me — no DB seeding) before choosing landing vs app.
  // The public landing must NOT block on /api/me, which runs first-request catalog seeding.
  if (!session) return <Splash />;
  const authed = session && session.authenticated;

  // QR self-enrolment deep link takes precedence over the landing/dashboard flow.
  if (joinSlug) {
    return (
      <>
        <Suspense fallback={<Center><T comp="div" style={{ color: C.dim }}>Loading…</T></Center>}>
          <JoinPage slug={joinSlug} me={me} onSignIn={() => setShowAuth(true)} />
        </Suspense>
        {showAuth && <AuthPanel onClose={() => setShowAuth(false)} onAuthed={onAuthed} />}
      </>
    );
  }

  // Public landing — renders immediately; needs no /api/me.
  // A /studio/<slug> deep link skips the landing and goes straight to the Studio (it still
  // requires identity below, so an anonymous prod visitor is redirected to login by the server).
  if (!authed && !entered && !studioSlug) {
    return (
      <>
        <Landing
          health={health}
          onSignIn={() => setShowAuth(true)}
          onPreview={(role) => { if (role) switchRole(role); setEntered(true); }}
        />
        {showAuth && <AuthPanel onClose={() => setShowAuth(false)} onAuthed={onAuthed} />}
      </>
    );
  }

  // From here we render a dashboard/studio — that needs the identity payload (/api/me).
  if (err) {
    return (
      <Center>
        <AlertTriangle size={26} color={C.red} />
        <T comp="div" style={{ color: C.hi }}>Couldn't load your account.</T>
        <div style={{ color: C.dim, fontFamily: "var(--mono)", fontSize: 12 }}>{err}</div>
        <button style={btn(C.indigo)} onClick={load}><RefreshCw size={13} /> <T>Retry</T></button>
      </Center>
    );
  }
  if (!me) return <Splash />;

  // Studio takes over the viewport (it has its own chrome); float a back control.
  if (studio) {
    return (
      <div style={{ position: "absolute", inset: 0 }}>
        <Suspense fallback={<Center><T comp="div" style={{ color: C.dim }}>Loading…</T></Center>}>
          <Studio slug={studio.slug} />
        </Suspense>
        <button style={styles.back} onClick={() => { setStudio(null); load(); }}>
          <ChevronLeft size={15} /> <T>Dashboard</T>
        </button>
      </div>
    );
  }

  const Dash = DASHBOARDS[me.dashboard] || ParticipantDashboard;

  return (
    <div style={styles.app}>
      <style>{css}</style>
      <header style={styles.top}>
        <div style={styles.brand}>
          <div style={styles.logo}><Brain size={15} color={C.bg} /></div>
          <div>
            <div style={styles.name}>Onset<span style={{ color: C.dim, fontWeight: 400 }}> · {t(ROLE_META[me.appRole]?.blurb || "Studio")}</span></div>
            <div style={styles.sub}>{me.name} · <span style={{ fontFamily: "var(--mono)" }}>{me.email}</span></div>
          </div>
        </div>

        <div style={styles.right}>
          <Badge color={ROLE_META[me.appRole]?.c}>{t(ROLE_META[me.appRole]?.label || me.appRole).toUpperCase()}</Badge>

          {/* Language picker (hash-based i18n). */}
          <LanguagePicker compact style={styles.select} />

          {/* Role switcher. Authenticated: "Act as" a role you're entitled to (your account's
              ceiling + every lower role). Dev: preview any of the four roles. */}
          {(() => {
            const roleOptions = authed ? (me.assumableRoles && me.assumableRoles.length ? me.assumableRoles : [me.appRole]) : ROLE_ORDER;
            const locked = authed && roleOptions.length <= 1;     // e.g. a plain participant
            return (
              <label style={styles.switch} title={authed ? t("Act as a role you're entitled to") : t("Dev role preview")}>
                <T comp="span" style={{ color: C.dim, fontSize: 11 }}>{authed ? "act as" : "view as"}</T>
                <select
                  value={me.appRole}
                  disabled={locked}
                  onChange={(e) => switchRole(e.target.value)}
                  style={{ ...styles.select, opacity: locked ? 0.5 : 1 }}
                >
                  {roleOptions.map((r) => <option key={r} value={r}>{t(ROLE_META[r] ? ROLE_META[r].label : r)}</option>)}
                </select>
              </label>
            );
          })()}

          {authed
            ? <button style={btnGhost} onClick={signOut}><LogOut size={13} /> <T>Sign out</T></button>
            : <button style={btnGhost} onClick={() => setShowAuth(true)}><LogIn size={13} /> <T>Sign in</T></button>}
        </div>
      </header>

      {showAuth && <AuthPanel onClose={() => setShowAuth(false)} onAuthed={onAuthed} />}

      <Suspense fallback={<Center><T comp="div" style={{ color: C.dim }}>Loading…</T></Center>}>
        <Dash me={me} onOpenStudio={(slug) => setStudio({ slug: slug || null })} />
      </Suspense>
    </div>
  );
}

// Branded loading splash — runs the live paradigm showcase while we resolve identity, so the
// (brief) wait is engaging instead of a blank spinner. It eases out as the landing/app fades in.
function Splash() {
  return (
    <Center>
      <div className="splash-in" style={{ width: "min(440px, 92vw)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, justifyContent: "center", marginBottom: 16 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, display: "grid", placeItems: "center" }}>
            <Brain size={16} color={C.bg} />
          </div>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.hi, fontFamily: "var(--ui)" }}>Onset<span style={{ color: C.dim, fontWeight: 400 }}> · Experiment Studio</span></div>
        </div>
        <HeroStage />
        <div style={{ textAlign: "center", color: C.dim, fontSize: 12, fontFamily: "var(--mono)", marginTop: 14 }}>
          <T>Loading…</T>
        </div>
      </div>
    </Center>
  );
}

function Center({ children }) {
  return (
    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", background: C.bg }}>
      <style>{css}</style>
      {children}
    </div>
  );
}

const btn = (bg) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: "none", background: bg, color: "#06102e", fontWeight: 700, fontSize: 13, cursor: "pointer" });
const btnGhost = { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.mid, fontWeight: 600, fontSize: 12, cursor: "pointer" };

const styles = {
  app: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", background: C.bg, color: C.hi, fontFamily: "var(--ui)" },
  top: { display: "flex", alignItems: "center", gap: 16, padding: "10px 18px", borderBottom: `1px solid ${C.border}`, background: C.panel, flexShrink: 0 },
  brand: { display: "flex", alignItems: "center", gap: 11, minWidth: 0 },
  logo: { width: 28, height: 28, borderRadius: 8, background: `linear-gradient(135deg, ${C.indigo}, ${C.purple})`, display: "grid", placeItems: "center", flexShrink: 0 },
  name: { fontWeight: 800, fontSize: 15, letterSpacing: -0.2 },
  sub: { color: C.dim, fontSize: 11 },
  right: { marginInlineStart: "auto", display: "flex", alignItems: "center", gap: 12 },
  switch: { display: "flex", alignItems: "center", gap: 6 },
  select: { background: "#0A0D11", border: `1px solid ${C.border}`, borderRadius: 7, color: C.hi, padding: "5px 8px", fontSize: 12, fontFamily: "var(--ui)", cursor: "pointer" },
  back: { position: "fixed", insetInlineStart: 14, bottom: 46, display: "inline-flex", alignItems: "center", gap: 4, padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.raised, color: C.hi, fontWeight: 600, fontSize: 12.5, cursor: "pointer", zIndex: 20, boxShadow: "0 6px 20px rgba(0,0,0,0.4)" },
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=IBM+Plex+Mono:wght@400;600&display=swap');
:root{ --ui:'Inter',system-ui,-apple-system,'Segoe UI',sans-serif; --mono:'IBM Plex Mono',ui-monospace,'SF Mono',Menlo,monospace; }
*{ box-sizing:border-box; }
body{ margin:0; }
@keyframes spin{ to{ transform:rotate(360deg); } }
.spin{ animation:spin .9s linear infinite; }
@keyframes floatK{ 0%,100%{ transform:translateY(0);} 50%{ transform:translateY(-8px);} }
.float{ animation:floatK 5s ease-in-out infinite; }
@keyframes popK{ 0%{ opacity:0; transform:scale(0.92);} 100%{ opacity:1; transform:scale(1);} }
.pop{ animation:popK .28s ease; }
@keyframes splashIn{ from{ opacity:0; transform:translateY(8px) scale(.98);} to{ opacity:1; transform:none;} }
.splash-in{ animation:splashIn .4s ease; }
@media (prefers-reduced-motion: reduce){ .float,.pop,.splash-in{ animation:none; } }
::-webkit-scrollbar{ width:9px; height:9px; }
::-webkit-scrollbar-thumb{ background:${C.border}; border-radius:6px; }
::-webkit-scrollbar-track{ background:transparent; }
button:focus-visible, select:focus-visible{ outline:2px solid ${C.indigo}; outline-offset:1px; }
button:hover{ filter:brightness(1.08); }
table tbody tr:hover td{ background:${C.rail}; }
`;
